import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  CheckCircle,
  Plus,
  EyeOff,
  Briefcase,
  MapPin,
  Clock,
  RefreshCw,
} from 'lucide-react'
import { toast } from '@/stores/toastStore'
import {
  useMatchResults,
  useSubmitFeedback,
  useArchiveMatch,
  useDeleteMatch,
} from '@/hooks/useAIMatching'
import { useMatchingStore } from '@/stores/matchingStore'
import { MatchResultCard } from '@/components/recruiter/ai/MatchResultCard'
import Button from '@/components/common/Button'
import Card from '@/components/common/Card'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import QuotaBar from '@/components/recruiter/QuotaBar'
import recruiterJobService from '@/services/recruiterJobService'
import jobProspectService from '@/services/jobProspectService'
import { RecruiterJob, EmploymentType, WorkMode } from '@/types'

const EMPLOYMENT_TYPE_LABELS: Record<EmploymentType, string> = {
  FULL_TIME: 'Full Time',
  PART_TIME: 'Part Time',
  CONTRACT: 'Contract',
  INTERNSHIP: 'Internship',
}

const WORK_MODE_LABELS: Record<WorkMode, string> = {
  REMOTE: 'Remote',
  HYBRID: 'Hybrid',
  ONSITE: 'Onsite',
}

export default function AIMatchResults() {
  const { matchId } = useParams<{ matchId: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation('aiMatching')

  const { selectedPage, setSelectedPage } = useMatchingStore()

  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean
    matchResultId: string | null
  }>({
    isOpen: false,
    matchResultId: null,
  })
  const [matchJob, setMatchJob] = useState<RecruiterJob | null>(null)
  const [excludeProspects, setExcludeProspects] = useState(false)

  // Reset page when matchId changes
  useEffect(() => {
    if (matchId) {
      setSelectedPage(1)
    }
  }, [matchId, setSelectedPage])

  // Fetch match results using React Query - show all candidates (minScore = 0)
  const {
    data: matchResults,
    isLoading,
    error,
  } = useMatchResults(matchId, selectedPage, 0, true, excludeProspects)

  // Load the recruiter job from the first result's recruiterJobId
  useEffect(() => {
    const recruiterJobId = matchResults?.results?.[0]?.recruiterJobId
    if (!recruiterJobId) return
    recruiterJobService
      .getJob(recruiterJobId)
      .then(setMatchJob)
      .catch(() => {
        /* non-fatal */
      })
  }, [matchResults?.results])

  // Mutations
  const submitFeedbackMutation = useSubmitFeedback()
  const archiveMatchMutation = useArchiveMatch()
  const deleteMatchMutation = useDeleteMatch()

  // Handle API errors
  useEffect(() => {
    if (error) {
      const apiError = error as { response?: { data?: { message?: string }; status?: number } }
      toast.error(apiError.response?.data?.message || t('notifications.loadFailed'))

      if (apiError.response?.status === 404) {
        navigate('/recruiter/ai-matching')
      }
    }
  }, [error, navigate, t])

  const handleFeedback = async (
    _resultId: string,
    candidateId: string,
    feedback: 'RELEVANT' | 'NOT_RELEVANT' | 'SHORTLISTED',
    notes?: string
  ) => {
    if (!matchId) return

    try {
      await submitFeedbackMutation.mutateAsync({
        matchId,
        candidateId,
        feedback,
        notes,
      })

      if (feedback === 'SHORTLISTED') {
        toast.success(t('notifications.feedbackShortlisted'), 4000)
      } else {
        toast.success(t('notifications.feedbackSuccess'))
      }
    } catch (error: unknown) {
      const apiError = error as { response?: { status?: number; data?: { message?: string } } }

      if (apiError.response?.status === 404) {
        toast.error(t('notifications.notFound'))
      } else if (apiError.response?.status === 403) {
        toast.error(t('notifications.unauthorized'))
      } else {
        toast.error(apiError.response?.data?.message || t('notifications.feedbackFailed'))
      }
    }
  }

  const handleArchiveMatch = async (matchResultId: string) => {
    try {
      await archiveMatchMutation.mutateAsync(matchResultId)
      toast.success(t('notifications.archiveSuccess'))
    } catch (error: unknown) {
      const apiError = error as { response?: { data?: { message?: string } } }
      toast.error(apiError.response?.data?.message || t('notifications.archiveFailed'))
    }
  }

  const handleDeleteMatchClick = (matchResultId: string) => {
    setDeleteConfirmation({ isOpen: true, matchResultId })
  }

  const handleDeleteMatchConfirm = async () => {
    const matchResultId = deleteConfirmation.matchResultId
    if (!matchResultId) return

    try {
      await deleteMatchMutation.mutateAsync(matchResultId)
      toast.success(t('notifications.deleteSuccess'))
      setDeleteConfirmation({ isOpen: false, matchResultId: null })
    } catch (error: unknown) {
      const apiError = error as { response?: { data?: { message?: string } } }
      toast.error(apiError.response?.data?.message || t('notifications.deleteFailed'))
    }
  }

  const handleAddToProspects = async (candidateId: string, aiMatchResultId: string) => {
    const recruiterJobId = matchJob?.id
    if (!recruiterJobId) {
      toast.error('Could not determine job for this match')
      return
    }
    try {
      await jobProspectService.addProspect({
        candidateId,
        recruiterJobId,
        source: 'AI_MATCH',
        aiMatchResultId,
      })
      toast.success('Candidate added to prospects')
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string; message?: string } } }
      const msg =
        err.response?.data?.error || err.response?.data?.message || 'Failed to add prospect'
      toast.error(msg)
      throw error // re-throw so the card can revert its state
    }
  }

  if (isLoading && !matchResults) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    )
  }

  if (!matchResults) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="text-center py-12">
          <p className="text-gray-600">{t('results.noResults')}</p>
          <Button onClick={() => navigate('/recruiter/ai-matching')} className="mt-4">
            {t('navigation.backToAIMatching')}
          </Button>
        </Card>
      </div>
    )
  }

  return (
    <>
      <QuotaBar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <Button
            onClick={() => navigate('/recruiter/ai-matching')}
            variant="secondary"
            className="mb-4 inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('navigation.backToAIMatching')}
          </Button>

          <div className="flex items-center justify-between flex-wrap gap-3">
            <h1 className="text-3xl font-bold text-gray-900">{t('results.title')}</h1>
            <div className="flex items-center gap-2">
              {matchJob && (
                <Button
                  onClick={() => navigate(`/recruiter/ai-matching/create?jobId=${matchJob.id}`)}
                  variant="primary"
                  className="inline-flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Search Again
                </Button>
              )}
              {matchJob && (
                <Button
                  onClick={() => navigate(`/recruiter/jobs/${matchJob.id}`)}
                  variant="secondary"
                  className="inline-flex items-center gap-2"
                >
                  <Briefcase className="w-4 h-4" />
                  View Job
                </Button>
              )}
              {matchJob && (
                <button
                  onClick={() => {
                    setExcludeProspects(!excludeProspects)
                  }}
                  className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium border transition-colors ${
                    excludeProspects
                      ? 'bg-purple-600 text-white border-purple-700'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <EyeOff className="w-4 h-4" />
                  Hide already-added prospects
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Job summary */}
        {matchJob && (
          <div className="mb-6 p-3 bg-gray-50 border border-gray-100">
            <div className="flex flex-wrap items-center gap-3">
              <Briefcase className="w-4 h-4 text-blue-600 flex-shrink-0" />
              <span className="font-semibold text-gray-900">{matchJob.title}</span>
              {matchJob.position && (
                <span className="text-sm text-gray-600">{matchJob.position}</span>
              )}
              {matchJob.location && (
                <span className="flex items-center gap-1 text-sm text-gray-500">
                  <MapPin className="w-3 h-3" />
                  {matchJob.location}
                </span>
              )}
              {matchJob.employmentType && (
                <span className="text-xs font-medium px-2 py-0.5 bg-blue-100 text-blue-700">
                  {EMPLOYMENT_TYPE_LABELS[matchJob.employmentType] ?? matchJob.employmentType}
                </span>
              )}
              {matchJob.workMode && (
                <span className="text-xs font-medium px-2 py-0.5 bg-purple-100 text-purple-700">
                  {WORK_MODE_LABELS[matchJob.workMode] ?? matchJob.workMode}
                </span>
              )}
              {matchResults.pagination.total > 0 && (
                <span className="flex items-center gap-1 text-sm text-gray-500">
                  <Clock className="w-3 h-3" />
                  {matchResults.pagination.total} candidates
                </span>
              )}
            </div>
            {matchJob.extractedSkills && matchJob.extractedSkills.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {matchJob.extractedSkills.map((skill) => (
                  <span
                    key={skill}
                    className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Results */}
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : matchResults.results.length === 0 && matchResults.pagination.total === 0 ? (
          <Card className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('noCandidatesFound.title')}
            </h3>
            <p className="text-gray-600 mb-4">{t('noCandidatesFound.description')}</p>
            <div className="flex justify-center mb-6">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200">
                <CheckCircle className="w-4 h-4" />
                {t('noCandidatesFound.refundedBadge')}
              </span>
            </div>
            <p className="text-sm text-gray-500 mb-6">{t('noCandidatesFound.hint')}</p>
            <Button
              onClick={() => navigate('/recruiter/ai-matching/create')}
              variant="primary"
              className="inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              {t('noCandidatesFound.tryAgain')}
            </Button>
          </Card>
        ) : (
          <>
            <div className="space-y-4 mb-6">
              {matchResults.results.map((result, index) => (
                <MatchResultCard
                  key={result.id}
                  result={result}
                  rank={result.rankPosition || (matchResults.pagination.page - 1) * 20 + index + 1}
                  onFeedback={async (feedback, notes) => {
                    await handleFeedback(result.id, result.candidateId, feedback, notes)
                  }}
                  onArchive={async () => {
                    await handleArchiveMatch(result.id)
                  }}
                  onDelete={async () => {
                    await handleDeleteMatchClick(result.id)
                  }}
                  onAddToProspects={matchJob ? handleAddToProspects : undefined}
                />
              ))}
            </div>

            {/* Pagination */}
            {matchResults.pagination.totalPages > 1 && (
              <div className="flex items-center justify-center gap-2">
                <Button
                  onClick={() => setSelectedPage(selectedPage - 1)}
                  disabled={selectedPage === 1}
                  variant="secondary"
                >
                  {t('results.pagination.previous')}
                </Button>
                <span className="text-sm text-gray-700">
                  {t('results.pagination.page', {
                    current: matchResults.pagination.page,
                    total: matchResults.pagination.totalPages,
                  })}
                </span>
                <Button
                  onClick={() => setSelectedPage(selectedPage + 1)}
                  disabled={selectedPage >= matchResults.pagination.totalPages}
                  variant="secondary"
                >
                  {t('results.pagination.next')}
                </Button>
              </div>
            )}
          </>
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={deleteConfirmation.isOpen}
          onClose={() => setDeleteConfirmation({ isOpen: false, matchResultId: null })}
          onConfirm={handleDeleteMatchConfirm}
          title={t('confirmations.deleteMatch.title')}
          message={t('confirmations.deleteMatch.message')}
          confirmText={t('confirmations.deleteMatch.confirm')}
          cancelText={t('confirmations.deleteMatch.cancel')}
          isDestructive={true}
          isLoading={deleteMatchMutation.isPending}
        />
      </div>
    </>
  )
}
